import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { processHttpError } from "@core/services/utils/process-http-error";
import { TranslateService } from "@ngx-translate/core";
import {
  DPMUserInvitationToken,
  DPMUserOnboardingRequest,
  InviteeTokenUI,
  SeverityLevel,
  TokenStateEnum,
} from "@services/business/_models/interfaces/invitations.interfaces";
import { BEInvitationsService } from "@services/business/invitations-services/be-invitations.service";
import { ConfirmationDialogComponent } from "@ui/components/confirmation-dialog/confirmation-dialog.component";
import { firstValueFrom } from "rxjs";

export interface OnboardingMessageState {
  title: string;
  message: string;
  level: SeverityLevel;
}

@Injectable({
  providedIn: "root",
})
export class OnboardingFacadeService {
  private readonly invitationServices = inject(BEInvitationsService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);

  private static readonly PIN_LENGTH = 5;

  protected readonly tokenState: WritableSignal<string> = signal("");
  protected readonly tokenUIState: WritableSignal<InviteeTokenUI | undefined> =
    signal(undefined);
  protected readonly fullTokenState: WritableSignal<
    DPMUserInvitationToken | undefined
  > = signal(undefined);
  protected readonly isLoadingState: WritableSignal<boolean> = signal(false);
  protected readonly pinValidatedState: WritableSignal<boolean> = signal(false);
  protected readonly lastPinState: WritableSignal<string> = signal("");
  protected readonly resultStateSignal: WritableSignal<
    OnboardingMessageState | undefined
  > = signal(undefined);
  protected readonly inlineMessageState: WritableSignal<
    OnboardingMessageState | undefined
  > = signal(undefined);

  public readonly token = this.tokenState.asReadonly();
  public readonly tokenUI = this.tokenUIState.asReadonly();
  public readonly fullToken = this.fullTokenState.asReadonly();
  public readonly isLoading = this.isLoadingState.asReadonly();
  public readonly pinValidated = this.pinValidatedState.asReadonly();
  public readonly lastPin = this.lastPinState.asReadonly();
  public readonly resultState = this.resultStateSignal.asReadonly();
  public readonly inlineMessage = this.inlineMessageState.asReadonly();

  async loadToken(token: string): Promise<void> {
    if (!this.isValidToken(token)) {
      this.setResultMessage(
        this.translateService.instant("Error.Error"),
        this.translateService.instant("Onboarding.InvitationNotRecognized"),
        SeverityLevel.error
      );
      return;
    }

    if (this.tokenState() !== token) {
      this.resetTokenContext(token);
    }

    if (this.tokenUIState()) {
      return;
    }

    this.isLoadingState.set(true);

    try {
      const response = await this.invitationServices.getTokenInfoInvitee(token);

      if (response.status !== TokenStateEnum.live) {
        this.setResultMessage(
          this.translateService.instant("Error.Error"),
          this.translateService.instant("Onboarding.InvitationExpired"),
          SeverityLevel.error
        );
        return;
      }

      this.tokenUIState.set(response);
    } catch (error) {
      processHttpError(error);
      this.setResultMessage(
        this.translateService.instant("Error.Error"),
        this.translateService.instant("Onboarding.InvalidInvitation"),
        SeverityLevel.error
      );
    } finally {
      this.isLoadingState.set(false);
    }
  }

  async requestPin(): Promise<void> {
    const token = this.tokenState();

    if (!this.isValidToken(token)) {
      this.setInlineMessage(
        this.translateService.instant("Error.Error"),
        this.translateService.instant("Onboarding.InvitationNotRecognized"),
        SeverityLevel.error
      );
      return;
    }

    this.clearInlineMessage();
    this.isLoadingState.set(true);

    try {
      await this.invitationServices.getPINOnboarding(token);
      this.setInlineMessage(
        this.translateService.instant("Onboarding.PinRequested"),
        this.translateService.instant("Onboarding.PinRequestMessage"),
        SeverityLevel.info
      );
    } catch (error) {
      processHttpError(error);
      const typedError = error as { message?: string };
      this.setInlineMessage(
        this.translateService.instant("Onboarding.ErrorRequestingPin"),
        typedError?.message ??
          this.translateService.instant("Error.UnexpectedError"),
        SeverityLevel.error
      );
    } finally {
      this.isLoadingState.set(false);
    }
  }

  async validatePin(pin: string): Promise<boolean> {
    const token = this.tokenState();

    if (!this.isValidPin(pin)) {
      this.setInlineMessage(
        this.translateService.instant("Error.Error"),
        this.translateService.instant("Onboarding.IncorrectPin", { 0: pin }),
        SeverityLevel.error
      );
      this.pinValidatedState.set(false);
      this.lastPinState.set("");
      return false;
    }

    if (this.lastPinState() === pin && this.pinValidatedState()) {
      return true;
    }

    this.clearInlineMessage();
    this.isLoadingState.set(true);
    this.lastPinState.set(pin);
    this.pinValidatedState.set(false);
    this.fullTokenState.set(undefined);

    try {
      const response = await this.invitationServices.getTokenInfoChecked(
        token,
        pin
      );
      this.fullTokenState.set(response);
      this.pinValidatedState.set(true);
      return true;
    } catch (error) {
      const typedError = error as { status?: number; message?: string };
      if (typedError.status !== 404) {
        processHttpError(error);
      }

      this.pinValidatedState.set(false);
      this.lastPinState.set("");

      this.setInlineMessage(
        this.translateService.instant("Error.Error"),
        typedError?.status === 404
          ? this.translateService.instant("Onboarding.IncorrectPin", {
              0: pin,
            })
          : (typedError?.message ??
              this.translateService.instant("Error.UnexpectedError")),
        SeverityLevel.error
      );
      return false;
    } finally {
      this.isLoadingState.set(false);
    }
  }

  async register(request: DPMUserOnboardingRequest): Promise<boolean> {
    const token = this.tokenState();
    const pin = this.lastPinState();

    this.clearInlineMessage();
    this.isLoadingState.set(true);

    try {
      await this.invitationServices.acceptOnboarding(token, pin, request);
      this.setResultMessage(
        this.translateService.instant("Onboarding.RegistrationCompleted"),
        this.translateService.instant(
          "Onboarding.RegistrationCompletedMessage"
        ),
        SeverityLevel.info
      );
      return true;
    } catch (error) {
      processHttpError(error);
      const typedError = error as { message?: string };
      this.setInlineMessage(
        this.translateService.instant("Onboarding.Attention"),
        typedError?.message ??
          this.translateService.instant("Onboarding.RegistrationNotCompleted"),
        SeverityLevel.error
      );
      return false;
    } finally {
      this.isLoadingState.set(false);
    }
  }

  showResultMessage(
    title: string,
    message: string,
    level: SeverityLevel
  ): void {
    this.setResultMessage(title, message, level);
  }

  clearResultMessage(): void {
    this.resultStateSignal.set(undefined);
  }

  clearInlineMessage(): void {
    this.inlineMessageState.set(undefined);
  }

  async confirmAbandon(onConfirm: () => void): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "350px",
      data: {
        message: this.translateService.instant(
          "Onboarding.AbandonProcessConfirmation"
        ),
        confirmButtonText: this.translateService.instant("App.Abandon"),
        cancelButtonText: this.translateService.instant("App.Cancel"),
        purpose: "leave",
      },
    });

    try {
      const result = await firstValueFrom(dialogRef.afterClosed());
      if (result) {
        onConfirm();
      }
    } catch (error) {
      processHttpError(error);
    }
  }

  private resetTokenContext(token: string): void {
    this.tokenState.set(token);
    this.tokenUIState.set(undefined);
    this.fullTokenState.set(undefined);
    this.pinValidatedState.set(false);
    this.lastPinState.set("");
    this.clearInlineMessage();
    this.clearResultMessage();
  }

  private setResultMessage(
    title: string,
    message: string,
    level: SeverityLevel
  ): void {
    this.resultStateSignal.set({
      title,
      message,
      level,
    });
  }

  private setInlineMessage(
    title: string,
    message: string,
    level: SeverityLevel
  ): void {
    this.inlineMessageState.set({
      title,
      message,
      level,
    });
  }

  private isValidToken(token: string): boolean {
    return Boolean(token && token.trim().length > 0);
  }

  private isValidPin(pin: string): boolean {
    return Boolean(
      pin && pin.trim().length === OnboardingFacadeService.PIN_LENGTH
    );
  }
}
